import {
  QuickActionOrigin,
  QuickActions,
} from "@faire/help-center-utils/components/QuickActions/QuickActions";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { hasShortcutsAccess } from "@faire/help-center-utils/serialized-data/shortcutsAccess";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsL1IssueClick } from "@faire/web-api--source/events/cxFoundations/click/l1Issue";
import { trackCxFoundationsTopicSelectPageView } from "@faire/web-api--source/events/cxFoundations/view/topicSelectPage";
import { QueryParameters as supportSelfServeParams } from "@faire/web-api--source/routes/www/support/self-serve";
import React, { useEffect } from "react";
import styled from "styled-components";

import { IssueTopics } from "../../components/IssueTopics";
import { useClearNodesInLocalStorageOnLoad } from "../../hooks/useClearNodesInLocalStorageOnLoad";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useSkipNodeSelection } from "../../hooks/useSkipNodeSelection";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { useTree } from "../../hooks/useTree";
import { QueryState, SelfServeNode } from "../../types";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const HomeWithShortcuts: React.FC<QueryState> = ({
  isLoading,
  dataUpdatedAt,
}) => {
  useTrackPageViewEffect(trackCxFoundationsTopicSelectPageView);
  useClearNodesInLocalStorageOnLoad();
  const { isMobile } = useViewport();
  const { tree } = useTree();
  const { level1Node } = useSelectedNode();
  const { setLevel1Node } = useNodeSetter();
  const { goToNextStep } = useNavigation();
  const goToNextStepRef = React.useRef(goToNextStep);
  goToNextStepRef.current = goToNextStep;
  const retailerOrBrandToken = getRetailerOrBrandToken();
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_home",
    enableTrackPerformanceBreakdown: "internal-tracking",
    isPageLoading: isLoading,
    pageLoadKey: "help_center-page_self_serve_home",
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    dataUpdatedAt,
  });

  const { brand_order_token: brandOrderToken } = useQueryParams(
    supportSelfServeParams.parse
  );
  const { skipOrderSelection, nodeSelected, setNodeSelected } =
    useSkipNodeSelection();

  useEffect(() => {
    if (brandOrderToken) {
      setNodeSelected(true);
    }
  }, [brandOrderToken, setNodeSelected]);

  useEffect(() => {
    if (nodeSelected && level1Node) {
      goToNextStepRef.current({ skipOrderSelection });
      setNodeSelected(false);
    }
  }, [nodeSelected, level1Node, skipOrderSelection, setNodeSelected]);

  const handleNodeSelection = (node: SelfServeNode) => {
    setLevel1Node(node);
    setNodeSelected(true);
    trackCxFoundationsL1IssueClick(node.id, retailerOrBrandToken);
  };

  return (
    <GridWrapper data-test-id="self-serve-home-wrapper">
      <IssueTopicsWrapper>
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            defaultMessage="What can we help with?"
            description={{
              text: "Title for the page that provides a series of relevant issue topics for users to select",
            }}
          />
        </Typography>
        <Spacer height="3x" />
        <IssueTopics
          parentNode={tree["Root"]}
          selectedNode={level1Node}
          setSelectedNode={handleNodeSelection}
          loadTimingRef={loadTimingRef}
        />
      </IssueTopicsWrapper>
      {isMobile || !hasShortcutsAccess() ? null : (
        <>
          <GridSpacer />
          <ShortcutsWrapper>
            <QuickActions origin={QuickActionOrigin.SelfServe} />
          </ShortcutsWrapper>
        </>
      )}
    </GridWrapper>
  );
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${getSpacing("2x")};
  padding: ${getSpacing("4x", "6x")};
`;

const IssueTopicsWrapper = styled.div`
  grid-column: span 8;
  ${responsiveMediaQueries.mobile} {
    grid-column: span 12;
  }
`;

const ShortcutsWrapper = styled.div`
  margin-top: -12px;
  grid-column: span 3;
  ${responsiveMediaQueries.tablet} {
    grid-column: span 4;
  }
`;

const GridSpacer = styled.div`
  grid-column: span 1;
  ${responsiveMediaQueries.tablet} {
    display: none;
  }
`;
